import {
  accountMutationHeaders,
  decodeAccountMutationAuthorizationV1,
  readBoundedAccountJson,
  refreshBrowserAccountStateSessionRepository,
} from "./account-state-session-repository.js";

const ACCOUNT_DELETION_PATH = "/api/v1/account/deletion";
const ACCOUNT_DELETION_AUTHORIZATION_MAX_BYTES = 8 * 1024;
const ACCOUNT_DELETION_ERROR_MAX_BYTES = 8 * 1024;
const ACCOUNT_DELETION_COMMAND = Object.freeze({
  schemaVersion: 1,
  confirmation: "deleteAccount",
});

function deletionUrlFromApiBaseUrl(apiBaseUrl) {
  let url;
  try {
    url = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Account deletion API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(url.protocol)
    || url.username
    || url.password
    || url.pathname !== "/"
    || url.search
    || url.hash
  ) {
    throw new TypeError("Account deletion API base URL must be an HTTP origin.");
  }
  return `${url.origin}${ACCOUNT_DELETION_PATH}`;
}

function result(kind) {
  return Object.freeze({ kind });
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, expected) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const fields = expected.slice().sort();
  return actual.length === fields.length
    && actual.every((field, index) => field === fields[index]);
}

function decodeDeletionAuthorization(value) {
  if (
    !hasExactFields(value, ["schemaVersion", "mutationAuthorization"])
    || value.schemaVersion !== 1
  ) {
    throw new TypeError("Account deletion authorization is invalid.");
  }
  const mutationAuthorization = decodeAccountMutationAuthorizationV1(
    value.mutationAuthorization,
  );
  if (mutationAuthorization.kind !== "cookieCsrf") {
    throw new TypeError("Browser account deletion requires cookie CSRF authorization.");
  }
  return Object.freeze({ kind: "ready", mutationAuthorization });
}

async function isRecentAuthenticationRequired(response) {
  try {
    const value = await readBoundedAccountJson(
      response,
      "Account deletion authorization error",
      ACCOUNT_DELETION_ERROR_MAX_BYTES,
    );
    return isRecord(value?.error)
      && value.error.code === "FORBIDDEN"
      && hasExactFields(value.error.details, ["reason"])
      && value.error.details.reason === "recentAuthenticationRequired";
  } catch (_error) {
    return false;
  }
}

export function createAccountDeletionRepository({
  apiBaseUrl,
  verifyCurrentIdentity,
  refreshSharedSession = refreshBrowserAccountStateSessionRepository,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (typeof verifyCurrentIdentity !== "function") {
    throw new TypeError("Account deletion requires a current-identity verifier.");
  }
  if (typeof refreshSharedSession !== "function") {
    throw new TypeError("Account deletion requires a shared-session refresh owner.");
  }
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Account deletion fetch implementation is unavailable.");
  }

  const deletionUrl = deletionUrlFromApiBaseUrl(apiBaseUrl);
  let disposed = false;
  let requestRevision = 0;
  let requestController = null;
  let inFlight = null;

  async function startDeletion() {
    requestRevision += 1;
    const revision = requestRevision;
    const controller = new AbortController();
    requestController = controller;
    let postAttempted = false;
    try {
      const authorizationResponse = await fetchImpl(deletionUrl, {
        method: "GET",
        cache: "no-store",
        credentials: "include",
        headers: { Accept: "application/json" },
        redirect: "error",
        signal: controller.signal,
      });
      if (disposed || revision !== requestRevision) return result("cancelled");
      if (authorizationResponse.status === 401) return result("signedOut");
      if (authorizationResponse.status === 403) {
        const recentAuthenticationRequired = await isRecentAuthenticationRequired(
          authorizationResponse,
        );
        if (disposed || revision !== requestRevision || controller.signal.aborted) {
          return result("cancelled");
        }
        return recentAuthenticationRequired
          ? result("reauthenticationRequired")
          : result("retryable");
      }
      if (!authorizationResponse.ok) return result("retryable");
      const authorization = decodeDeletionAuthorization(
        await readBoundedAccountJson(
          authorizationResponse,
          "Account deletion authorization",
          ACCOUNT_DELETION_AUTHORIZATION_MAX_BYTES,
        ),
      );
      if (disposed || revision !== requestRevision) return result("cancelled");

      const identityState = await verifyCurrentIdentity(controller.signal);
      if (disposed || revision !== requestRevision || controller.signal.aborted) {
        return result("cancelled");
      }
      if (identityState === "signedOut") return result("signedOut");
      if (identityState === "changed") return result("identityRefreshRequired");
      if (identityState === "retryable") return result("retryable");
      if (identityState !== "current") {
        throw new TypeError("Account deletion identity verification is invalid.");
      }

      postAttempted = true;
      const response = await fetchImpl(deletionUrl, {
        method: "POST",
        cache: "no-store",
        credentials: "include",
        headers: accountMutationHeaders(authorization),
        redirect: "error",
        signal: controller.signal,
        body: JSON.stringify(ACCOUNT_DELETION_COMMAND),
      });
      if (disposed || revision !== requestRevision) return result("cancelled");
      if (response.status === 204) return result("deleted");
      if (response.status === 202) return result("deletionPending");
      if (response.status === 401) return result("signedOut");
      if (response.status === 403) {
        const recentAuthenticationRequired = await isRecentAuthenticationRequired(response);
        if (disposed || revision !== requestRevision || controller.signal.aborted) {
          return result("cancelled");
        }
        return recentAuthenticationRequired
          ? result("reauthenticationRequired")
          : result("identityRefreshRequired");
      }
      return result("retryable");
    } catch (error) {
      if (
        disposed
        || revision !== requestRevision
        || controller.signal.aborted
        || error?.name === "AbortError"
      ) {
        return result("cancelled");
      }
      return result("retryable");
    } finally {
      if (postAttempted) {
        try {
          Promise.resolve(refreshSharedSession()).catch(() => {});
        } catch (_error) {
          // This is cache invalidation only; the deletion response remains authoritative.
        }
      }
      if (requestController === controller) requestController = null;
    }
  }

  function deleteAccount() {
    if (disposed) return Promise.resolve(result("cancelled"));
    if (!inFlight) {
      inFlight = startDeletion().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestRevision += 1;
    requestController?.abort();
    requestController = null;
  }

  return Object.freeze({ deleteAccount, dispose });
}
